import * as React from "react";
import { Alert, AlertTitle, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import CclGenericAccordian from "../../../components/common/cclGenericAccordian";
import DetailPage, { DetailPageTab } from "../../../components/common/detailPage/detailPage";
import { SimpleBreadcrumbs } from "../../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../../layouts/standard";
import PayrollNotes from "../../sessions/sessionDetail/payroll/payrollNotes";
import { cclTokenedPayrollApi } from "../../../services/cclTokenedPayrollApi";
import PayrollProjectFacultyDetails from "./facultyDetails";
import PayrollProjectSummary from "./summary";
import PayrollProjectWorkDetails from "./workDetails";
import { PayrollProject } from "./payrollProjectDetailsTypes";

const PayrollProjectDetails: React.FC = () => {
    const { projectId = "" } = useParams<{ projectId: string }>();
    const [showFacultyDetails, setShowFacultyDetails] = React.useState(false);

    React.useEffect(() => {
        setShowFacultyDetails(false);
    }, [projectId]);

    const { currentData: projectData, isFetching: projectLoading, isError: projectError } =
        cclTokenedPayrollApi.useGetPayrollProjectByIdQuery(projectId, {
            skip: !projectId,
            refetchOnMountOrArgChange: true,
        });
    const project: PayrollProject = {
        ...projectData,
        projectId,
        customer: projectData?.customer ?? projectData?.client,
        projectCode: projectData?.projectCode ?? projectData?.eventCode ?? projectId,
    };
    const projectName = project.projectName ?? project.sessionTitle ?? projectId;
    const projectCode = project.projectCode ?? projectId;
    const sessionId = project.sessionId ?? "";
    const [getWorkEntryDetails, { data: workEntryData, isLoading: workEntryDetailsLoading, isError }] =
        cclTokenedPayrollApi.useLazyGetWorkEntryByIdQuery();

    const displayWorkEntryDetails = (workEntryId: number) => {
        setShowFacultyDetails(true);
        getWorkEntryDetails(String(workEntryId))
            .unwrap()
            .catch((error) => console.error("Failed to load faculty details for work entry:", error));
    };

    const breadcrumbs = (
        <Stack direction="row" spacing={3}>
            <SimpleBreadcrumbs>
                <Typography variant="h6" color="text.primary">
                    <Link key="payrollLink" component={RouterLink} to="/payroll">
                        Payroll
                    </Link>
                </Typography>
                <Typography variant="h6" color="text.primary">
                    {project.projectName + ` (${project.projectId})` || project.sessionTitle + ` (${project.projectId})`|| "Project Details" }
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    if (!projectId || projectError || (!projectLoading && !projectData)) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {projectError ? "Unable to load payroll project details." : "Project details were not found."}
                </Alert>
            </StandardLayout>
        );
    }

    if (projectLoading && !projectData) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <Stack alignItems="center" sx={{ p: 3 }}>
                    <CircularProgress aria-label="Loading payroll project details" />
                </Stack>
            </StandardLayout>
        );
    }

    const tablist: DetailPageTab[] = [
        {
            label: "Project Payroll",
            index: 0,
            content: (
                <Stack sx={{ width: 1, height: 1, m: 1 }} spacing={2}>
                    <CclGenericAccordian headingText="Summary" defaultExpanded noGutter sx={{ width: 1 }}>
                        <PayrollProjectSummary key={projectId} project={project} editable />
                    </CclGenericAccordian>
                    <CclGenericAccordian headingText="Work Entries" defaultExpanded noGutter sx={{ width: 1 }}>
                        <PayrollProjectWorkDetails
                            projectId={projectId}
                            projectName={projectName}
                            projectCode={projectCode}
                            sessionId={sessionId}
                            revRecDate={project.revRecDate}
                            onViewFacultyDetails={displayWorkEntryDetails}
                            loadingFacultyDetails={workEntryDetailsLoading}
                        />
                    </CclGenericAccordian>
                    {showFacultyDetails && (
                        <CclGenericAccordian headingText="Faculty Details" defaultExpanded noGutter sx={{ width: 1 }}>
                            <PayrollProjectFacultyDetails
                                projectCode={projectCode}
                                sessionId={sessionId}
                                workEntryData={isError ? undefined : workEntryData}
                            />
                        </CclGenericAccordian>
                    )}
                    <CclGenericAccordian headingText="Notes" defaultExpanded noGutter sx={{ width: 1 }}>
                        <PayrollNotes
                            payrollKey={projectId}
                            projectCode={projectCode}
                            sessionId={sessionId}
                        />
                    </CclGenericAccordian>
                </Stack>
            ),
        },
    ];

    return (
        <DetailPage
            tablist={tablist}
            breadcrumbs={breadcrumbs}
            descriptor="payroll-project"
            identifier={projectId}
        />
    );
};

export default PayrollProjectDetails;
